/**
 * Copyright 2024-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useFormContext, useWatch } from "react-hook-form";
import { Input, theme, FlexContainer } from "@nordcloud/gnui";
import { FormGroup } from "~/components";
import { EditButtons } from "~/components/Details/InlineEdit/EditButtons";
import { FullWidthContainer } from "~/components/Styled";
import { getFirstItem } from "~/tools";
import { PlanEntityNameSchema } from "~/views/plans/schemas";
import {
  getActionDisplayName,
  getDisplayNameByActionType,
} from "~/views/plans/utils";
import { PlanField } from "../../../constants";
import { usePlanWizard } from "../../../PlanProvider";
import { PlanEntity, SelectedPlanEntity } from "../../../types";
import { useStepState } from "../../StepProvider";
import { updateEntities } from "../utils";

export function PlanActionNameInput({
  item,
  handlePlanErrors,
  onEditComplete,
}: {
  item: PlanEntity;
  handlePlanErrors: () => void;
  onEditComplete: () => void;
}) {
  const actionListId = item.listId;

  const {
    control,
    formState: { errors },
    setError,
    clearErrors,
    trigger,
    handleSubmit,
    register,
  } = useFormContext();

  const { setPlanData } = usePlanWizard();

  const { stepState, updateStepState } = useStepState();

  const updateSelectedAction = (updateObject: Partial<SelectedPlanEntity>) => {
    const updatedEntities = updateEntities(stepState, updateObject);
    updateStepState({
      entities: updatedEntities,
    });
    setPlanData((prevPlanData) => ({
      ...prevPlanData,
      [PlanField.PLAN_SETTINGS]: {
        planEntities: updatedEntities,
      },
    }));
  };

  const handleActionError = (name: string, listId?: string) => {
    if (!listId) {
      return;
    }

    const isValid = PlanEntityNameSchema.safeParse(name);
    if (!isValid.success) {
      setError(listId, {
        message: getFirstItem(isValid.error.issues).message,
      });
    } else {
      clearErrors(listId);
    }
  };

  const onSubmit = (data: { [key: string]: string }) => {
    if (actionListId) {
      const newName = data[actionListId].trim();
      const fallbackName = item.action?.name ?? item.actionName ?? item.name;
      const finalName = newName.length === 0 ? fallbackName : newName;

      updateSelectedAction({
        name: finalName,
      });
    }
    handlePlanErrors();
    onEditComplete();
  };

  const placeholder = getDisplayNameByActionType(
    item.action?.name || item.actionName || "",
    item.actionType
  );

  const displayName = getActionDisplayName(
    item.name || "",
    item.action?.name || item.actionName || "",
    item.actionType
  );

  const currentName = useWatch({
    control,
    name: actionListId ?? "",
    defaultValue: displayName,
  });

  if (!actionListId) {
    return null;
  }

  const handleSave = () => {
    if (currentName !== item.name && currentName.trim() !== displayName) {
      handleSubmit(onSubmit)();
    } else {
      onEditComplete();
    }
  };

  return (
    <FormGroup
      error={errors[actionListId]}
      customCss={{
        marginBottom: theme.spacing.spacing03,
        marginTop: theme.spacing.spacing03,
      }}
    >
      <FlexContainer alignItems="center">
        <FullWidthContainer>
          <Input
            small
            id={actionListId}
            defaultValue={displayName}
            placeholder={placeholder}
            {...register(actionListId, {
              onChange: (e) => {
                handleActionError(e.target.value, actionListId);
                handlePlanErrors();
              },
              onBlur: () => {
                trigger();
              },
            })}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSave();
              }
            }}
          />
        </FullWidthContainer>
        <EditButtons onClose={onEditComplete} onSave={handleSave} />
      </FlexContainer>
    </FormGroup>
  );
}
